import React from 'react';
import { motion } from 'framer-motion';
import { Rocket, Shield, Zap, Star } from 'lucide-react';

const ProofSection = () => {
  const stats = [
    {
      icon: Rocket,
      value: '50+',
      label: 'MVPs Launched',
      color: 'text-primary'
    },
    {
      icon: Shield,
      value: '1000+',
      label: 'Security Vulnerabilities Fixed',
      color: 'text-blue-500'
    },
    {
      icon: Zap,
      value: '30+',
      label: 'WordPress Sites Automated',
      color: 'text-purple-500'
    },
    {
      icon: Star,
      value: '100%',
      label: 'Client Satisfaction',
      color: 'text-amber-500'
    }
  ];

  const testimonials = [
    {
      quote: "Lovabuild transformed our AI-generated MVP from a security nightmare into a production-ready app. Their vibe-check audit caught issues we didn't even know existed.",
      author: "Sarah Chen",
      role: "Founder, TechStart AI",
      avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop"
    },
    {
      quote: "The WordPress AI plugins they built have saved us 20 hours a week on content optimization. Our SEO has never been better.",
      author: "Marcus Johnson",
      role: "Marketing Director, GrowthCo",
      avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop"
    },
    {
      quote: "Fast, affordable, and secure. They built our internal dashboard in under 2 weeks for less than we'd pay a single developer.",
      author: "Emily Rodriguez",
      role: "COO, ScaleUp Inc",
      avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop"
    }
  ];

  return (
    <section className="py-20 bg-muted/30">
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
            Trusted by Founders & Agencies <span className="text-primary">Who Build Fast</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Join the innovators who've chosen Lovabuild to build, secure, and automate their vision
          </p>
        </motion.div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16 max-w-5xl mx-auto">
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-card/50 backdrop-blur border border-border rounded-xl p-6 text-center hover:border-primary/50 transition-all duration-300"
              >
                <Icon className={`w-8 h-8 ${stat.color} mx-auto mb-3`} />
                <div className="text-3xl font-bold mb-1">{stat.value}</div>
                <div className="text-sm text-muted-foreground">{stat.label}</div>
              </motion.div>
            );
          })}
        </div>

        {/* Testimonials Carousel */}
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-card/50 backdrop-blur border border-border rounded-xl p-6 hover:border-primary/50 transition-all duration-300"
              >
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-amber-500 text-amber-500" />
                  ))}
                </div>
                
                <p className="text-muted-foreground mb-6 italic">
                  "{testimonial.quote}"
                </p>
                
                <div className="flex items-center gap-3">
                  <img 
                    src={testimonial.avatar} 
                    alt={testimonial.author}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                  <div>
                    <div className="font-semibold text-sm">{testimonial.author}</div>
                    <div className="text-xs text-muted-foreground">{testimonial.role}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProofSection;
